import type { ChallengeService } from '@duck/api-definitions'

export type Ev = ChallengeService.ActivityReportEvent

/** A gap this long between consecutive events splits the report into separate sittings. */
export const BREAK_MS = 30 * 60_000
const MINUTE = 60_000
export const INSERT_TYPES = new Set<string>(['paste', 'drop', 'inject'])
export const isInsert = (e: Ev) => INSERT_TYPES.has(e.type)

export interface Session {
  events: Ev[]
  start: number
  end: number
  /** Total typing time, summed over typing stretches. */
  activeMs: number
  activeMinutes: number
  typed: number
  pasted: number
  /** Typing stretches, sorted. */
  spans: { start: number; end: number }[]
}

export interface SessionSplit {
  sessions: Session[]
  /** breaks[i] = ms between sessions[i] and sessions[i + 1] */
  breaks: number[]
}

/**
 * How long a typing stretch ran. Legacy 'active' events were per-minute buckets with no duration, so
 * they fall back to a minute, but never past the next thing the user did (saves are server-side and
 * happen mid-typing, so they do not cut a stretch short).
 */
export const typingMs = (e: Ev, events: readonly Ev[] = [], index = -1): number => {
  const nominal = e.durationMs ?? MINUTE
  // `events` is sorted; start right after e instead of scanning from the top.
  for (let j = index + 1; j < events.length; j++) {
    const n = events[j]
    if (n.t > e.t && n.type !== 'save') return Math.min(nominal, n.t - e.t)
  }
  return nominal
}

const build = (events: Ev[]): Session => {
  const spans = events.flatMap((e, i) => (e.type === 'active' ? [{ start: e.t, end: e.t + typingMs(e, events, i) }] : []))
  const activeMs = spans.reduce((s, x) => s + (x.end - x.start), 0)
  const last = events[events.length - 1]
  return {
    events,
    start: events[0].t,
    end: last.type === 'active' ? last.t + typingMs(last, events, events.length - 1) : last.t,
    activeMs,
    activeMinutes: spans.length === 0 ? 0 : Math.max(1, Math.round(activeMs / MINUTE)),
    typed: events.filter((e) => e.type === 'active').reduce((s, e) => s + (e.len ?? 0), 0),
    pasted: events.filter(isInsert).reduce((s, e) => s + (e.len ?? 0), 0),
    spans,
  }
}

export const splitSessions = (events: readonly Ev[]): SessionSplit => {
  const sorted = [...events].sort((a, b) => a.t - b.t)
  const groups: Ev[][] = []
  const breaks: number[] = []
  for (const e of sorted) {
    const current = groups[groups.length - 1]
    const prev = current?.[current.length - 1]
    if (prev === undefined || e.t - prev.t < BREAK_MS) {
      if (current === undefined) groups.push([e]); else current.push(e)
    } else {
      breaks.push(e.t - prev.t)
      groups.push([e])
    }
  }
  return { sessions: groups.map(build), breaks }
}

/**
 * Typing time (ms) elapsed inside the session before moment t, so idle stretches take no width on a
 * chart. Falls back to wall-clock share when nothing was typed.
 */
export const activePosition = (s: Session, t: number): number => {
  if (s.spans.length === 0) return s.end > s.start ? (t - s.start) / (s.end - s.start) : 0
  let pos = 0
  for (const x of s.spans) pos += Math.max(0, Math.min(t, x.end) - x.start)
  return pos
}
